/**
 * Decide quais erros JS viram evento no Sentry.
 *
 * Na web o SDK embrulha os callbacks de `addEventListener` da página inteira,
 * inclusive os de script que não é nosso: as extensões de carteira cripto
 * (`inpage.js`) e as extensões de navegador que escapam do `denyUrls`. Quando
 * esse código de terceiro quebra, o erro chega no projeto como se fosse do app.
 *
 * Dois vizinhos que NÃO entram na lista, e o porquê:
 *
 * - `bugsnag.min.js` não é de terceiro. O `rudder-sdk-js` v2, que
 *   `configs/rudderstack.web.ts` importa, injeta
 *   `https://d2wy8f7a9ursnm.cloudfront.net/v6/bugsnag.min.js` em toda página
 *   web nossa. Depois do rewrite ele chega idêntico ao bugsnag que o navegador
 *   da smart TV injeta, e a telemetria do RudderStack embrulha listener de XHR:
 *   descartar por esse frame esconderia erro real de aluno (HET-4006, e a seção
 *   "Sentry" do CLAUDE.md do serviço).
 * - `iabjs://` é do in-app browser do Instagram/Facebook, mas o filtro dele é
 *   por mensagem E origem (`isInjectedLoggerError` abaixo, HET-3937): a mesma
 *   origem com outra mensagem continua valendo evento.
 *
 * O critério é deny-list, não allow-list: os nossos próprios chunks de web
 * viram nome genérico na pilha (`app:///200.js`), então tentar reconhecer "o
 * que é nosso" descartaria erro de verdade. Cai fora o evento cujo frame mais
 * interno com dono é de arquivo alheio conhecido.
 *
 * Por que o frame mais interno e não "a pilha inteira": o `beforeSend` roda no
 * cliente DEPOIS dos event processors e ANTES de qualquer symbolication, e o
 * `createReactNativeRewriteFrames` (integração default, ativa também na web)
 * reescreve todo `filename` para `app:///<basename>` e apaga o `abs_path`.
 * Então o wrapper de `addEventListener` do próprio SDK chega como
 * `app:///helpers.js` — indistinguível de um chunk nosso, e exigir que a pilha
 * inteira fosse alheia nunca descartaria nada em produção.
 */

import type { Event } from '@sentry/react-native'

/** Arquivos que nunca são nossos: presença na pilha prova erro de terceiro. */
const FOREIGN_SCRIPTS = [
  // Extensões de carteira cripto (MetaMask e clones).
  /(^|\/)inpage\.js/i,
  // chrome-extension://, moz-extension://, safari-web-extension://. Só pega
  // evento que escapou do rewrite de frames: depois dele a extensão chega
  // como `app:///content.js`, sem jeito de distinguir de chunk nosso.
  /-extension:\/\//i,
]

/**
 * Frames que não dizem de quem é o erro: eval e código nativo. Mesma ideia do
 * `_getLastValidUrl` do SDK (usado pelo `denyUrls`), mas por regex e não por
 * igualdade exata — é justamente por isso que `denyUrls` não substitui este
 * módulo: depois do rewrite o frame chega como `app:///<anonymous>`, que o
 * `!== '<anonymous>'` do SDK não pula. (`node_modules/@sentry/` também não
 * entra: o rewrite já transformou isso em `app:///helpers.js`.)
 */
const OWNERLESS_FRAMES = [/<anonymous>/, /\[native code\]/]

function matchesAny(filename: string, patterns: RegExp[]): boolean {
  return patterns.some((pattern) => pattern.test(filename))
}

/** Nomes de arquivo de todos os frames do evento, na ordem da pilha. */
function collectFilenames(event: Event): string[] {
  return (event.exception?.values ?? []).flatMap((value) =>
    (value.stacktrace?.frames ?? []).map((frame) => frame.filename ?? ''),
  )
}

/**
 * `true` quando o frame mais interno com dono é de script alheio conhecido.
 * Pilha sem nenhum frame com dono (ou sem pilha) passa — não dá pra atribuir.
 */
export function isForeignScriptEvent(event: Event): boolean {
  const filenames = collectFilenames(event)

  // De dentro pra fora: quem jogou o erro é o ÚLTIMO frame da pilha. Isso é
  // garantido pelo parser do SDK, que inverte o stack justamente pra isso
  // (@sentry/core, utils/stacktrace.js: "returns the reverse representation so
  // call site of the function that caused the crash is the last frame").
  for (let index = filenames.length - 1; index >= 0; index -= 1) {
    const filename = filenames[index]

    if (filename === '' || matchesAny(filename, OWNERLESS_FRAMES)) continue

    return matchesAny(filename, FOREIGN_SCRIPTS)
  }

  return false
}

/**
 * `true` para o `ReferenceError: Locale is not defined` (HET-4006), lançado por
 * script sem URL — código avaliado em runtime, injetado no navegador do aluno.
 *
 * Precisa de teste próprio porque a pilha dele não tem NENHUM frame com dono
 * (só `<anonymous>`), e `isForeignScriptEvent` não chuta nesse caso: pilha sem
 * dono passa. Aqui a atribuição vem da assinatura, não da pilha — a página web
 * carrega um script só (`entry-<hash>.js`), o Metro nunca emite `<anonymous>` e
 * `Locale` não existe como identificador livre no nosso código nem no bundle
 * publicado.
 *
 * Exigir mensagem E frame na mesma exceção mantém o filtro colado na
 * assinatura: `<anonymous>` também é nome de frame nativo em erro legítimo
 * nosso (`JSON.parse(<anonymous>)`), e mensagem sozinha barraria esse caso.
 */
export function isUndefinedLocaleError(event: Event): boolean {
  return (event.exception?.values ?? []).some(
    (exception) =>
      exception.value === 'Locale is not defined' &&
      (exception.stacktrace?.frames ?? []).some((frame) =>
        /^(?:app:\/\/\/)?<anonymous>$/.test(frame.filename ?? ''),
      ),
  )
}

/**
 * `true` para a falha da bridge do logger que o in-app browser do
 * Instagram/Facebook injeta (HET-3937).
 *
 * Por mensagem E origem, e não pelo frame sozinho: o `iabjs://` hospeda o
 * nosso app dentro do in-app browser, então outra mensagem vinda da mesma
 * origem ainda pode ser bug nosso. O prefixo `app:///` é opcional porque o
 * rewrite de frames roda antes deste hook.
 */
export function isInjectedLoggerError(event: Event): boolean {
  return (event.exception?.values ?? []).some(
    (exception) =>
      exception.value === 'Error invoking postMessage: Java object is gone' &&
      (exception.stacktrace?.frames ?? []).some((frame) =>
        /^(?:app:\/\/\/)?iabjs:\/\/navigation_performance_logger_android$/.test(
          frame.filename ?? '',
        ),
      ),
  )
}
